import React from 'react';
import moment from 'moment';

import {Button, Dialogue, Icon, Modal, Text} from '@albert-io/atomic';
import ConfirmActionModal from 'client/components/ConfirmActionModal/ConfirmActionModal.react';
import CustomizeReassignModal from 'client/components/CustomizeReassignModal/CustomizeReassignModal.react';
import {AddAssignmentQuestionSetsToFolderModal} from 'client/Classrooms/Classroom/Teacher/Assignments/AssignmentsList/AssignmentRowOptions.react';
import {invalidatePartialInterest, genericMandarkRequest} from 'resources/mandark.resource';

import type {AssignmentDeleteScope} from './AssignmentMenu.helpers';
import {
  buildDeleteRequest,
  getDeleteConfirmationCopy,
  getInvalidateResourcePath
} from './AssignmentMenu.helpers';

export type AssignmentModalType = 'details' | 'delete' | 'reassign' | 'folder' | 'archived' | null;

interface AssignmentActionModalsProps {
  modal: AssignmentModalType;
  onClose: () => void;
  assignmentId: string;
  assignmentName: string;
  isDraft: boolean;
  /** What a delete would take away; resolved by the menu before it opens the delete modal. */
  deleteScope: AssignmentDeleteScope | null;
  /** Resolved creator name for the details modal (fetched by the menu before opening). */
  creatorName: string | null;
  updatedAt: string;
  /** Question counts for "Save to Folder"; archivedQuestionCount drives filter_archived on save. */
  questionCount: number;
  archivedQuestionCount: number;
  onDeleted: () => void;
}

export const AssignmentActionModals = ({
  modal,
  onClose,
  assignmentId,
  assignmentName,
  isDraft,
  deleteScope,
  creatorName,
  updatedAt,
  questionCount,
  archivedQuestionCount,
  onDeleted
}: AssignmentActionModalsProps) => {
  const deleteRequest = deleteScope
    ? buildDeleteRequest({assignmentId, isDraft, scope: deleteScope})
    : null;

  const deleteAssignment = async () => {
    if (!deleteRequest) {
      return;
    }
    const {resourcePath, body} = deleteRequest;
    await genericMandarkRequest('delete', {resourcePath}, body);
  };

  const handleDeleteResolved = () => {
    invalidatePartialInterest({resourcePath: getInvalidateResourcePath(isDraft)});
    onClose();
    onDeleted();
  };

  if (modal === 'details') {
    return (
      <Modal handleClose={onClose} ariaLabel='Document Details'>
        {({modalContentStyle}) => (
          <Dialogue inModal handleClose={onClose} size='s' className={modalContentStyle}>
            <Text size='m' bold>
              Document Details
            </Text>
            <div className='mt-x3 mb-x2 gap-x1 flex items-center'>
              <Icon icon='user' iconStyle='regular' />
              Created by: {creatorName || '—'}
            </div>
            <div className='gap-x1 flex items-center'>
              <Icon icon='calendar' iconStyle='regular' />
              Last modified {moment(updatedAt).format('MMM D, YYYY')}
            </div>
          </Dialogue>
        )}
      </Modal>
    );
  }

  if (modal === 'delete' && deleteScope) {
    return (
      <ConfirmActionModal
        title='Are you sure?'
        align='center'
        handleClose={onClose}
        makeRequest={deleteAssignment}
        onResolve={handleDeleteResolved}
      >
        {({isRequestInflight, onCancel, onConfirm}) => (
          <>
            <Dialogue.Body align='center'>
              {getDeleteConfirmationCopy({assignmentName, isDraft, scope: deleteScope})}
            </Dialogue.Body>
            <Dialogue.BtnGroup align='center'>
              <Button color='secondary' disabled={isRequestInflight} onClick={onCancel}>
                Cancel
              </Button>
              <Button color='negative' disabled={isRequestInflight} onClick={onConfirm}>
                Delete
              </Button>
            </Dialogue.BtnGroup>
          </>
        )}
      </ConfirmActionModal>
    );
  }

  if (modal === 'reassign') {
    return (
      <CustomizeReassignModal
        assignmentId={assignmentId}
        assignmentName={assignmentName}
        handleClose={onClose}
      />
    );
  }

  if (modal === 'folder') {
    return (
      <AddAssignmentQuestionSetsToFolderModal
        assignmentId={assignmentId}
        handleClose={onClose}
        questionCount={questionCount}
        isDraft={isDraft}
        archivedQuestionCount={archivedQuestionCount}
      />
    );
  }

  return null;
};
